import {
  Component, Input, forwardRef, ElementRef, ViewChild, OnChanges
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { CdkConnectedOverlay, ConnectedPosition } from '@angular/cdk/overlay';

export interface DnaSelectOption {
  value: any;
  label: string;
}

@Component({
  selector: 'dna-search-select',
  templateUrl: './dna-search-select.component.html',
  styleUrls: ['./dna-search-select.component.scss'],
  standalone: false,
  providers: [{
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => DnaSearchSelectComponent),
    multi: true
  }]
})
export class DnaSearchSelectComponent implements ControlValueAccessor, OnChanges {
  @Input() options: DnaSelectOption[] = [];
  @Input() placeholder = 'Selecione...';
  @Input() disabled = false;
  @Input() multiple = false;
  @ViewChild(CdkConnectedOverlay) private _overlay: CdkConnectedOverlay;

  isOpen = false;
  searchTerm = '';
  selectedValue: any = null;
  selectedValues: any[] = [];

  // Largura do painel travada na largura do campo (lida na abertura).
  overlayWidth = 0;

  // Abre abaixo do campo; se não houver espaço, o CDK vira para cima.
  overlayPositions: ConnectedPosition[] = [
    { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top', offsetY: 4 },
    { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom', offsetY: -4 },
  ];

  private onChange = (_: any) => {};
  private onTouched = () => {};

  constructor(private _el: ElementRef) {}

  ngOnChanges(): void {}

  // Compara valores sem coerção frouxa: com `==` a opção vazia ('' = "Todas
  // as Situações") e a opção 0 ("Em Edição") eram consideradas o mesmo item e
  // ficavam marcadas juntas. Vazio ('' / null / undefined) é equivalente entre si.
  isSameValue(a: any, b: any): boolean {
    const isEmpty = (value: any) => value === null || value === undefined || value === '';
    if (isEmpty(a) || isEmpty(b)) return isEmpty(a) && isEmpty(b);
    return String(a) === String(b);
  }

  get selectedLabel(): string {
    if (this.multiple) {
      if (!this.selectedValues.length) return '';
      if (this.selectedValues.length === 1) {
        return this.options.find(o => this.isSameValue(o.value, this.selectedValues[0]))?.label || '';
      }
      return `${this.selectedValues.length} selecionados`;
    }
    return this.options.find(o => this.isSameValue(o.value, this.selectedValue))?.label || '';
  }

  get filteredOptions(): DnaSelectOption[] {
    if (!this.searchTerm.trim()) return this.options;
    const term = this.searchTerm.toLowerCase();
    return this.options.filter(o => o.label.toLowerCase().includes(term));
  }

  get isAllSelected(): boolean {
    return this.filteredOptions.length > 0 &&
      this.filteredOptions.every(o => this.isSelected(o.value));
  }

  isSelected(value: any): boolean {
    return this.selectedValues.some(selected => this.isSameValue(selected, value));
  }

  toggle(): void {
    if (this.disabled) return;
    if (this.isOpen) {
      this.close();
      return;
    }
    this.overlayWidth = this._el.nativeElement.getBoundingClientRect().width;
    this.isOpen = true;
    // O input fica dentro do overlay (fora do view do componente), então o
    // ViewChild não resolve; busca o input no DOM do overlay já anexado.
    setTimeout(() => {
      const input = this._overlay?.overlayRef?.overlayElement
        .querySelector('input') as HTMLInputElement | null;
      // preventScroll: no mobile, focar o input fazia o navegador rolar o
      // .cdk-overlay-pane horizontalmente p/ "trazê-lo à vista", cortando o
      // início das opções. Sem o scroll, o conteúdo nunca desloca.
      input?.focus({ preventScroll: true });
    });
  }

  close(): void {
    if (!this.isOpen) return;
    this.isOpen = false;
    this.searchTerm = '';
  }

  select(opt: DnaSelectOption, event: MouseEvent): void {
    event.stopPropagation();
    this.selectedValue = opt.value;
    this.onChange(opt.value);
    this.onTouched();
    this.close();
  }

  toggleOption(opt: DnaSelectOption, event: MouseEvent): void {
    event.stopPropagation();
    const idx = this.selectedValues.findIndex(value => this.isSameValue(value, opt.value));
    if (idx >= 0) {
      this.selectedValues.splice(idx, 1);
    } else {
      this.selectedValues.push(opt.value);
    }
    this.onChange([...this.selectedValues]);
    this.onTouched();
  }

  toggleAll(event: MouseEvent): void {
    event.stopPropagation();
    if (this.isAllSelected) {
      this.selectedValues = [];
    } else {
      this.selectedValues = this.filteredOptions.map(o => o.value);
    }
    this.onChange([...this.selectedValues]);
    this.onTouched();
  }

  onOutsideClick(event: MouseEvent): void {
    // Clique no próprio campo é tratado pelo toggle(); evita fechar e reabrir.
    if (this._el.nativeElement.contains(event.target as Node)) return;
    this.close();
  }

  writeValue(val: any): void {
    if (this.multiple) {
      this.selectedValues = Array.isArray(val) ? val : (val ? [val] : []);
    } else {
      this.selectedValue = val;
    }
  }
  registerOnChange(fn: any): void { this.onChange = fn; }
  registerOnTouched(fn: any): void { this.onTouched = fn; }
  setDisabledState(isDisabled: boolean): void { this.disabled = isDisabled; }
}
